import React, { useEffect, useState } from "react";
import { FormColourPicker, FormImageUploader, FormSwitch } from "components/form-fields";
import { ButtonSave } from "components/buttons/ButtonSave";
import { Button } from 'components/buttons/Button';
import { AlertUnsavedData, NotificationToaster, AlertConfirm } from "components/notifications";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { ResellerService } from "services/ResellerService";
import { AccountService } from "services/AccountService";
import { ShowHide } from "components/layout/ShowHide";
import { FailurePanel } from "components/notifications/FailurePanel";
import { Intent } from "@blueprintjs/core";
import { useDispatch } from "react-redux";

export function ResellerGeneralSettings() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [rawResellerSettings, setRawResellerSettings] = useState({});
    const [resellerSettings, setResellerSettings] = useState({
        logoUploaded: "",
        reportLogo: "",
        sidebarColour: "",
        sidebarTextColour: "",
        sidebarActiveMenuColour: ""
    });
    const [isValid, errors, validate] = useValidation();
    const isDataDirty = useDirtyData(rawResellerSettings, resellerSettings);
    // useDirtyData recurses into object values to compare them. When a logo is swapped, the field
    // value changes from a string (URL) to a File object, which has no enumerable own properties,
    // so the recursion reports it as unchanged. Detect logo changes explicitly via strict inequality
    // against the saved value so the Save button and unsaved-data alert enable on a logo-only change.
    const isWebsiteLogoDirty = resellerSettings.logoUploaded !== rawResellerSettings.logoUploaded;
    const isReportLogoDirty = resellerSettings.reportLogo !== rawResellerSettings.reportLogo;
    const isFormDirty = isDataDirty || isWebsiteLogoDirty || isReportLogoDirty;
    const [isLivePreviewEnabled, setIsLivePreviewEnabled] = useState(false);
    const [isErrorOnLoad, setIsErrorOnLoad] = useState(false);
    const [isResetThemeAlertVisible, setIsResetThemeAlertVisible] = useState(false);

    // The live-preview logo is stored in Redux so the TopBar can render it. A raw File cannot live
    // in Redux: it trips RTK's immutability check (File has a mutable lastModifiedDate) which breaks
    // the dispatch chain, and redux-persist serialises it to {} and rehydrates a stale value. Convert
    // a File to a data-URL string before dispatching; pass an existing URL string through unchanged.
    function getLogoPreviewSource(logo) {
        return new Promise((resolve) => {
            if (logo instanceof Blob) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve(event.target.result);
                };
                reader.onerror = () => {
                    resolve(null);
                };
                reader.readAsDataURL(logo);
            } else {
                resolve(logo);
            }
        });
    }

    useEffect(() => {
        fetchResellerSettings();
    }, []);

    useEffect(checkSettings, [
        isValid,
        isLoading,
        resellerSettings
    ]);

    function fetchResellerSettings() {
        ResellerService.getResellerSettings()
            .then((response) => {
                const mapped = {
                    ...response,
                    logoUploaded: response.logoUploadedUrl
                };
                setRawResellerSettings(mapped);
                setResellerSettings(mapped);
            }, () => {
                setIsErrorOnLoad(true);
            })
            .catch((error) => {
                setIsErrorOnLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }


    function checkSettings() {
        let rules = [];
        rules.push({ fieldName: "sidebarColour", required: true });
        rules.push({ fieldName: "sidebarTextColour", required: true });
        rules.push({ fieldName: "sidebarActiveMenuColour", required: true });

        validate(rules, resellerSettings);
    }

    function handleSave() {
        setIsSaving(true);

        let websiteLogoFile = resellerSettings.logoUploaded;
        let reportLogoFile = resellerSettings.reportLogo;

        if (websiteLogoFile === rawResellerSettings.logoUploaded || (!websiteLogoFile && !rawResellerSettings.logoUploaded)) {
            websiteLogoFile = null;
        }
        if (reportLogoFile === rawResellerSettings.reportLogo || (!reportLogoFile && !rawResellerSettings.reportLogo)) {
            reportLogoFile = null;
        }

        ResellerService.updateResellerSettings(
            websiteLogoFile,
            reportLogoFile,
            resellerSettings.sidebarColour,
            resellerSettings.sidebarTextColour,
            resellerSettings.sidebarActiveMenuColour
        )
            .then((response) => {
                setIsLivePreviewEnabled(false);
                dispatch({ type: "RESET_THEME_PREVIEW" });
                setRawResellerSettings(resellerSettings);
                getMetaData();

            }, (errorMessage) => {
                NotificationToaster.show(
                    Intent.DANGER,
                    errorMessage
                );
            })
            .catch((error) => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not save reseller settings. Please try again later."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function getMetaData() {
        AccountService.getMetadata()
            .then((response) => {
                NotificationToaster.show(Intent.SUCCESS, "Reseller settings saved successfully.");
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Sorry something has gone wrong. Please try refreshing the page.");
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Sorry something has gone wrong. Please try refreshing the page.");
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function onFileChangeLogo(file) {
        setResellerSettings((prev) => {
            return {
                ...prev,
                logoUploaded: file
            }
        });

        if (isLivePreviewEnabled) {
            getLogoPreviewSource(file).then((source) => {
                dispatch({ type: "SET_LOGO_UPLOADED_PREVIEW", payload: source });
            });
        }
    }

    function onFileReportLogo(file) {
        setResellerSettings((prev) => {
            return {
                ...prev,
                reportLogo: file
            }
        });
    }

    function onSideBarColourChange(colour) {
        setResellerSettings({ ...resellerSettings, sidebarColour: colour });
        if (isLivePreviewEnabled) {
            dispatch({ type: "SET_SIDEBAR_COLOUR_PREVIEW", payload: colour });
        }
    }

    function onSidebarTextColourChange(colour) {
        setResellerSettings({ ...resellerSettings, sidebarTextColour: colour });
        if (isLivePreviewEnabled) {
            dispatch({ type: "SET_SIDEBAR_TEXT_COLOUR_PREVIEW", payload: colour });
        }
    }

    function onSidebarActiveMenuColourChange(colour) {
        setResellerSettings({ ...resellerSettings, sidebarActiveMenuColour: colour });
        if (isLivePreviewEnabled) {
            dispatch({ type: "SET_SIDEBAR_ACTIVE_MENU_COLOUR_PREVIEW", payload: colour });
        }
    }

    function toggleLivePreview(event) {
        setIsLivePreviewEnabled(event.target.checked);

        if (event.target.checked) {
            dispatch({ type: "SET_SIDEBAR_COLOUR_PREVIEW", payload: resellerSettings.sidebarColour });
            dispatch({ type: "SET_SIDEBAR_TEXT_COLOUR_PREVIEW", payload: resellerSettings.sidebarTextColour });
            dispatch({ type: "SET_SIDEBAR_ACTIVE_MENU_COLOUR_PREVIEW", payload: resellerSettings.sidebarActiveMenuColour });
            getLogoPreviewSource(resellerSettings.logoUploaded).then((source) => {
                dispatch({ type: "SET_LOGO_UPLOADED_PREVIEW", payload: source });
            });
        } else {
            dispatch({ type: "RESET_THEME_PREVIEW" });
        }
    }

    function onDelete() {
        setIsSaving(true);

        ResellerService.resetTheme()
            .then((response) => {
                fetchResellerSettings();
                getMetaData();
            }, () => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not reset reseller settings. Please try again later."
                );
            })
            .catch((error) => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not reset reseller settings. Please try again later."
                );
            })
            .finally(() => {
                setIsSaving(false);
                setIsResetThemeAlertVisible(false);
            });
    }

    return (
        <div className="row">
            <ShowHide
                evaluator={isErrorOnLoad}
                show={<FailurePanel title="Failure to load reseller settings">
                    <p>
                        There was an error while loading the reseller settings. <br />
                        Please try again later.
                    </p>
                </FailurePanel>}
                hide={
                    <>
                        <h2>Theme</h2>
                        <FormSwitch
                            loading={isLoading}
                            disabled={isSaving || isLoading}
                            headingText="Live Preview"
                            helperText="Turning this on will allow you to preview the current styles you have selected"
                            checked={isLivePreviewEnabled}
                            onChange={(event) => toggleLivePreview(event)}
                        />
                        <FormImageUploader
                            isLoading={isLoading}
                            isDisabled={isSaving}
                            headingText="Website Logo"
                            helperText="Upload a PNG file that will replace the Vision logo in the top left hand corner of the website. Logo size should be 280px wide and 80px high."
                            dangerHelperText={errors.logoUploaded}
                            value={resellerSettings.logoUploaded || "https://vision.fta.co.uk/Content/images/svg/vision-logo.svg"}
                            onChange={onFileChangeLogo}
                            height={80}
                            width={280}
                            backgroundColor={resellerSettings.sidebarColour}
                            accept="image/png"
                            invalidFileTypeText="Please upload a PNG file."
                        />
                        <FormImageUploader
                            isLoading={isLoading}
                            isDisabled={isSaving}
                            headingText="Report Logo"
                            helperText="Upload a PNG file that will replace the Vision logo in the self service reports. Logo size should be 280px wide and 80px high."
                            dangerHelperText={errors.reportLogo}
                            value={resellerSettings.reportLogo || "/images/report-logo.jpg"}
                            onChange={onFileReportLogo}
                            height={80}
                            width={280}
                            accept="image/png"
                            invalidFileTypeText="Please upload a PNG file."
                        />
                        <FormColourPicker
                            isLoading={isLoading}
                            isDisabled={isSaving}
                            headingText="Sidebar Background Colour"
                            helperText="Select the background colour of the main application frame"
                            dangerHelperText={errors.sidebarColour}
                            value={resellerSettings.sidebarColour}
                            onChange={onSideBarColourChange}
                        />
                        <FormColourPicker
                            isLoading={isLoading}
                            isDisabled={isSaving}
                            headingText="Sidebar Text Colour"
                            helperText="Select the text colour of the left hand menu and top bar"
                            dangerHelperText={errors.sidebarTextColour}
                            value={resellerSettings.sidebarTextColour}
                            onChange={onSidebarTextColourChange}
                        />
                        <FormColourPicker
                            isLoading={isLoading}
                            isDisabled={isSaving}
                            headingText="Sidebar Active Menu Colour"
                            helperText="Select the colour for when a menu item section is active"
                            dangerHelperText={errors.sidebarActiveMenuColour}
                            value={resellerSettings.sidebarActiveMenuColour}
                            onChange={onSidebarActiveMenuColourChange}
                        />
                        <div className="inline-items">
                            <ButtonSave
                                onClick={handleSave}
                                disabled={isSaving}
                                simpleDisabled={!isFormDirty || !isValid}
                                loading={isLoading}
                            />
                            <Button
                                text="Reset Theme"
                                intent="danger"
                                onClick={() => setIsResetThemeAlertVisible(true)}
                                disabled={isSaving}
                                loading={isLoading}
                            />
                        </div>
                        <AlertUnsavedData
                            isDirty={isFormDirty}
                            scrollToBottomOnCancel={true}
                        />
                        <AlertConfirm
                            title="Reset Theme"
                            isOpen={isResetThemeAlertVisible}
                            onConfirm={onDelete}
                            onCancel={() => setIsResetThemeAlertVisible(false)}
                            confirmButtonText="Confirm Reset"
                            confirmButtonIntent="danger"
                        >
                            <p>Are you sure you want to reset the theme?</p>
                        </AlertConfirm>
                    </>
                }
            />
        </div>
    );
}